import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params } from '@angular/router';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, pluck, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AccountService } from '../../core/account.service';
import { Market } from '../../core/markets/market';
import { MarketsService } from '../../core/markets/markets.service';
import { UniqueIdToItemIdAndQuantityMap } from '../order-form-page/interface';
import { extractQueryParameters } from './utils';

/* The responsibility of this class is to provide hooks into url parameters */
@Injectable()
export class ActivationParamService {
  readonly accountGroupId$: Observable<string>;
  readonly marketId$: Observable<string>;
  // the order form is navigated to from several sources and the button navigate differently depending on the source
  // instead of wrapping the service in abstract classes
  readonly navigationSource$: Observable<string>;

  // Represents the quantity map obtained from the url
  readonly resellerItemQuantityMapFromUrl$: Observable<UniqueIdToItemIdAndQuantityMap>;
  private params$: ReplaySubject<Params> = new ReplaySubject<Params>(1);

  constructor(
    @Inject('PARTNER_ID') public readonly partnerId$: Observable<string>,
    private marketsService: MarketsService,
    private accountService: AccountService,
  ) {
    this.accountGroupId$ = this.params$.pipe(
      pluck('accountGroupId'),
      tap((accountGroupId) => this.accountService.setCurrentAccountID(accountGroupId, false)),
      distinctUntilChanged(),
      shareReplay(1),
    ) as Observable<string>;

    this.navigationSource$ = this.params$.pipe(
      pluck('navigationSource'),
      map((navSource) => navSource || 'account-create'),
      distinctUntilChanged(),
      shareReplay(1),
    ) as Observable<string>;

    const paramsMarketId$ = this.params$.pipe(pluck('marketId')) as Observable<string>;

    const marketIdFromAccount$ = this.accountGroupId$.pipe(
      switchMap((accountGroupId) => {
        this.accountService.setCurrentAccountID(accountGroupId);
        return this.accountService.account$;
      }),
      map((acc) => (acc ? acc.market_id : undefined)),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.marketId$ = combineLatest([paramsMarketId$, marketIdFromAccount$]).pipe(
      switchMap(([marketIdFromParams, marketIdFromAccount]) => {
        if (marketIdFromAccount) {
          return of(marketIdFromAccount);
        }
        if (marketIdFromParams) {
          return of(marketIdFromParams);
        } else {
          return this.marketsService.currentMarket$.pipe(map((market: Market) => market.market_id));
        }
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.resellerItemQuantityMapFromUrl$ = this.params$.pipe(map((params) => extractQueryParameters(params)));
  }

  resolve(route: ActivatedRouteSnapshot): string | null {
    this.params$.next({ ...route.params, ...route.queryParams });
    return route.params.accountGroupId;
  }
}
